import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    RiAddLine,
    RiEditLine,
    RiDeleteBinLine,
    RiEyeLine,
    RiArrowLeftLine,
    RiSearchLine,
    RiFilterLine,
    RiStarLine,
    RiStarFill
} from 'react-icons/ri';
import Button from '@/components/common/Button/Button';
import Input from '@/components/forms/Input/Input';
import Spinner from '@/components/common/Spinner/Spinner';
import { type AdminProduct } from '@/hooks/admin/useAdmin';
import { useProducts, useDeleteProduct } from '@/hooks/admin/useAdminProducts';
import ProductModal from '@/modals/admin/ProductModal';

const ProductsAdmin: React.FC = () => {
    const [selectedProduct, setSelectedProduct] = useState<AdminProduct | undefined>();
    const [modalOpen, setModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterActive, setFilterActive] = useState<string>('all');
    const [filterFeatured, setFilterFeatured] = useState<string>('all');

    const { data: productsResponse, isLoading, error } = useProducts({
        search: searchTerm || undefined,
        page_size: 50 // Show more products in admin
    });
    const deleteProduct = useDeleteProduct();

    const products = productsResponse?.results || [];

    const filteredProducts = products.filter(product => {
        const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            product.description.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesActive = filterActive === 'all' ||
                            (filterActive === 'active' && product.is_active) ||
                            (filterActive === 'inactive' && !product.is_active);
        const matchesFeatured = filterFeatured === 'all' ||
                              (filterFeatured === 'featured' && product.is_featured) ||
                              (filterFeatured === 'not-featured' && !product.is_featured);
        return matchesSearch && matchesActive && matchesFeatured;
    });

    const handleCreateProduct = () => {
        setSelectedProduct(undefined);
        setModalOpen(true);
    };

    const handleEditProduct = (product: AdminProduct) => {
        setSelectedProduct(product);
        setModalOpen(true);
    };

    const handleDeleteProduct = async (product: AdminProduct) => {
        if (window.confirm(`¿Estás seguro de que deseas eliminar el producto "${product.name}"?`)) {
            try {
                await deleteProduct.mutateAsync(product.id);
            } catch (error) {
                console.error('Error deleting product:', error);
                alert('Error al eliminar el producto');
            }
        }
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedProduct(undefined);
    };

    const handleModalSuccess = () => {
        // Modal will close itself on success
    };

    const formatPrice = (price: number) => {
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'USD'
        }).format(price);
    };

    if (isLoading) {
        return (
            <div className="admin-page-loading">
                <Spinner size="lg" />
                <p>Cargando productos...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-page-error">
                <p>Error al cargar los productos</p>
                <Button onClick={() => window.location.reload()}>
                    Reintentar
                </Button>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <div className="admin-page-header-left">
                    <Link to="/admin" className="admin-back-link">
                        <RiArrowLeftLine />
                        <span>Volver al Dashboard</span>
                    </Link>
                    <div>
                        <h1 className="admin-page-title">Gestión de Productos</h1>
                        <p className="admin-page-subtitle">
                            Administra el catálogo completo de productos personalizables
                        </p>
                    </div>
                </div>
                <Button
                    onClick={handleCreateProduct}
                    variant="primary"
                    className="admin-create-btn"
                >
                    <RiAddLine />
                    Nuevo Producto
                </Button>
            </div>

            {/* Filters and Search */}
            <div className="admin-filters">
                <div className="admin-search">
                    <Input
                        name="search"
                        value={searchTerm}
                        setValue={setSearchTerm}
                        placeholder="Buscar productos..."
                        startContent={<RiSearchLine />}
                    />
                </div>
                <div className="admin-filter-group">
                    <label className="admin-filter-label">
                        <RiFilterLine />
                        Estado:
                    </label>
                    <select
                        value={filterActive}
                        onChange={(e) => setFilterActive(e.target.value)}
                        className="admin-filter-select"
                    >
                        <option value="all">Todos</option>
                        <option value="active">Activos</option>
                        <option value="inactive">Inactivos</option>
                    </select>
                </div>
                <div className="admin-filter-group">
                    <label className="admin-filter-label">
                        Destacados:
                    </label>
                    <select
                        value={filterFeatured}
                        onChange={(e) => setFilterFeatured(e.target.value)}
                        className="admin-filter-select"
                    >
                        <option value="all">Todos</option>
                        <option value="featured">Destacados</option>
                        <option value="not-featured">No destacados</option>
                    </select>
                </div>
            </div>

            {/* Products Table */}
            <div className="admin-table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Imagen</th>
                            <th>Producto</th>
                            <th>Categoría</th>
                            <th>Precio Base</th>
                            <th>Stock</th>
                            <th>Estado</th>
                            <th>Destacado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredProducts.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="admin-table-empty">
                                    {searchTerm || filterActive !== 'all' || filterFeatured !== 'all'
                                        ? 'No se encontraron productos con los filtros aplicados'
                                        : 'No hay productos registrados'
                                    }
                                </td>
                            </tr>
                        ) : (
                            filteredProducts.map(product => (
                                <tr key={product.id}>
                                    <td className="admin-table-image">
                                        {product.main_image_url ? (
                                            <img
                                                src={product.main_image_url}
                                                alt={product.name}
                                                className="admin-product-image"
                                            />
                                        ) : (
                                            <div className="admin-no-image">
                                                <RiEyeLine />
                                            </div>
                                        )}
                                    </td>
                                    <td className="admin-table-product">
                                        <div className="admin-product-info">
                                            <div className="admin-product-name">
                                                {product.name}
                                            </div>
                                            <div className="admin-product-description">
                                                {product.short_description || 'Sin descripción corta'}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="admin-table-category">
                                        {product.category.name}
                                    </td>
                                    <td className="admin-table-price">
                                        {formatPrice(product.base_price)}
                                    </td>
                                    <td className="admin-table-stock">
                                        <span className={`admin-stock ${product.stock_quantity > 0 ? 'in-stock' : 'out-of-stock'}`}>
                                            {product.stock_quantity}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`admin-status ${product.is_active ? 'active' : 'inactive'}`}>
                                            {product.is_active ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </td>
                                    <td className="admin-table-featured">
                                        {product.is_featured ? (
                                            <RiStarFill className="admin-star-filled" />
                                        ) : (
                                            <RiStarLine className="admin-star-empty" />
                                        )}
                                    </td>
                                    <td className="admin-table-actions">
                                        <Button
                                            onClick={() => handleEditProduct(product)}
                                            variant="secondary"
                                            size="sm"
                                            className="admin-action-btn"
                                        >
                                            <RiEditLine />
                                            Editar
                                        </Button>
                                        <Button
                                            onClick={() => handleDeleteProduct(product)}
                                            variant="danger"
                                            size="sm"
                                            className="admin-action-btn"
                                            disabled={deleteProduct.isPending}
                                        >
                                            <RiDeleteBinLine />
                                            Eliminar
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {productsResponse && productsResponse.count > products.length && (
                <div className="admin-pagination">
                    <p>
                        Mostrando {products.length} de {productsResponse.count} productos
                    </p>
                    {/* Add pagination controls here if needed */}
                </div>
            )}

            {/* Product Modal */}
            <ProductModal
                isOpen={modalOpen}
                onClose={handleModalClose}
                product={selectedProduct}
                onSuccess={handleModalSuccess}
            />
        </div>
    );
};

export default ProductsAdmin;
